/**
 * AdminSessions — Sessions & Auth policy.
 *
 * Super-admin can:
 *  • see who is currently signed-in (users with a fresh last_login_at)
 *  • force-logout any single user (revokes their token via
 *    /api/security/force-logout/{user_id})
 *  • blast-revoke every non-admin session at once (incident response)
 *  • tune the access/refresh TTLs (default 30 min / 7 days)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Shield, ShieldOff, LogOut, RefreshCw, Save, Clock, KeyRound, AlertTriangle, History, X, MessageSquare, CheckCircle2, XCircle, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";
import LoginHistoryTimeline from "@/components/LoginHistoryTimeline";

function MessagingDiagnosticsCard() {
    const [diag, setDiag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testTo, setTestTo] = useState("");
    const [testMsg, setTestMsg] = useState("Hello from Law OS · WhatsApp delivery test.");
    const [testing, setTesting] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/messaging/diagnostics"); setDiag(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const runTest = async () => {
        if (!testTo.trim()) { toast.error("Enter a recipient phone"); return; }
        setTesting(true); setLastResult(null);
        try {
            const { data } = await api.post("/admin/messaging/test-whatsapp", { to: testTo.trim(), message: testMsg });
            setLastResult(data);
            if (data.ok) toast.success(`Sent via ${data.provider || "provider"} — check recipient's WhatsApp`);
            else toast.error(`Send failed via ${data.provider || "any provider"}. See raw response below.`);
        } catch (e) { toast.error(apiError(e)); setLastResult({ ok: false, error: apiError(e) }); }
        finally { setTesting(false); }
    };

    return (
        <div className="border border-border p-5" data-testid="messaging-diag-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-sm font-medium flex items-center gap-2"><MessageSquare className="w-4 h-4 text-primary"/> WhatsApp / SMS delivery</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Diagnose why OTPs {`"sent"`} but never arrived. Shows the dispatcher{`'`}s provider priority and fires a real test send.
                    </div>
                </div>
                <button onClick={load} data-testid="messaging-diag-refresh"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border border-border px-2.5 py-1.5 hover:border-primary">
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/> Refresh
                </button>
            </div>
            {!diag ? <div className="text-xs text-muted-foreground">Loading…</div> : (
                <>
                    <div className="text-[11px] mb-2">
                        <span className="uppercase tracking-widest text-muted-foreground mr-2">Will send via:</span>
                        <span className={diag.whatsapp.will_send.startsWith("none") ? "text-rose-400 font-mono" : "text-emerald-400 font-mono"}
                            data-testid="messaging-diag-active-provider">{diag.whatsapp.will_send}</span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2 mb-4">
                        {["meta_waba", "deropo", "legacy_platform_settings"].map(k => {
                            const p = diag.whatsapp[k];
                            return (
                                <div key={k} className="border border-border p-2.5 text-[11px]"
                                    data-testid={`messaging-diag-${k}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</span>
                                        {p.configured
                                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>
                                            : <XCircle className="w-3.5 h-3.5 text-muted-foreground"/>}
                                    </div>
                                    <div className={`text-xs mt-1 ${p.configured ? "text-emerald-400" : "text-muted-foreground"}`}>
                                        {p.configured ? "Configured" : "Not configured"}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-1">{p.hint}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="border-t border-border pt-3">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Fire a test WhatsApp</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input value={testTo} onChange={e => setTestTo(e.target.value)}
                                data-testid="messaging-diag-to"
                                placeholder="Phone (e.g., 9946006050)"
                                className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                            <input value={testMsg} onChange={e => setTestMsg(e.target.value)}
                                data-testid="messaging-diag-msg"
                                className="md:col-span-1 bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                            <button onClick={runTest} disabled={testing}
                                data-testid="messaging-diag-send"
                                className="inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest border border-primary bg-primary/10 text-primary hover:bg-primary/20 px-3 py-2 disabled:opacity-40">
                                <Send className="w-3 h-3"/> {testing ? "Sending…" : "Send test"}
                            </button>
                        </div>
                        <PhoneNormalizeRow/>
                        {lastResult && (
                            <div className={`mt-3 p-3 text-[11px] font-mono border ${lastResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}
                                data-testid="messaging-diag-result">
                                <div className="font-sans text-xs mb-1">
                                    {lastResult.ok ? <span className="text-emerald-400">✓ Sent via {lastResult.provider}</span>
                                        : <span className="text-rose-400">✗ Failed · status={lastResult.status || "unknown"}</span>}
                                </div>
                                <pre className="whitespace-pre-wrap overflow-x-auto">{JSON.stringify(lastResult.raw || lastResult, null, 2)}</pre>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}


function PhoneNormalizeRow() {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(null);
    const run = async (dryRun) => {
        setBusy(dryRun ? "preview" : "apply");
        try {
            const { data } = await api.post(`/admin/messaging/normalize-phones?dry_run=${dryRun}`);
            setPreview(data);
            if (dryRun) toast.info(`Preview: ${data.changed} phone(s) will be normalised · ${data.already_canonical} already canonical · ${data.unresolvable} unresolvable`);
            else toast.success(`Applied: ${data.changed} phone(s) normalised to E.164`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };
    return (
        <div className="mt-4 pt-3 border-t border-border" data-testid="phone-normalize-row">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-medium">Unify phone formats to E.164</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Fixes {`"no account found"`} when a user tries `9946006050` and their record is stored as `+919946006050` (or vice versa). Preview first, then apply.
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => run(true)} disabled={!!busy} data-testid="phone-normalize-preview"
                        className="text-[10px] uppercase tracking-widest border border-border hover:border-primary px-3 py-1.5 disabled:opacity-40">
                        {busy === "preview" ? "Scanning…" : "Preview"}
                    </button>
                    <button onClick={() => run(false)} disabled={!!busy || !preview || preview.changed === 0}
                        data-testid="phone-normalize-apply"
                        className="text-[10px] uppercase tracking-widest border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 px-3 py-1.5 disabled:opacity-40">
                        {busy === "apply" ? "Applying…" : preview ? `Apply (${preview.changed})` : "Apply"}
                    </button>
                </div>
            </div>
            {preview && (preview.sample_changes.length > 0 || preview.sample_unresolvable.length > 0) && (
                <div className="mt-2 text-[10px] font-mono border border-border p-2 bg-background max-h-48 overflow-y-auto"
                    data-testid="phone-normalize-preview-table">
                    {preview.sample_changes.map(c => (
                        <div key={c.user_id} className="flex justify-between gap-2 py-0.5">
                            <span className="text-muted-foreground truncate">{c.email}</span>
                            <span><span className="text-rose-400">{c.from}</span> → <span className="text-emerald-400">{c.to}</span></span>
                        </div>
                    ))}
                    {preview.sample_unresolvable.map(u => (
                        <div key={u.user_id} className="flex justify-between gap-2 py-0.5 text-amber-400">
                            <span>{u.email}</span>
                            <span>unresolvable: {JSON.stringify(u.raw)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function StatCard({ label, value, hint, testId }) {
    return (
        <div className="border border-border p-4" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="text-3xl font-serif mt-1">{value}</div>
            {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

function PolicyCard({ policy, onSave }) {
    const [accessMin, setAccessMin] = useState(policy.access_min);
    const [refreshDays, setRefreshDays] = useState(policy.refresh_days);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setAccessMin(policy.access_min); setRefreshDays(policy.refresh_days); }, [policy]);

    const dirty = accessMin !== policy.access_min || refreshDays !== policy.refresh_days;

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/session-policy", { access_min: Number(accessMin), refresh_days: Number(refreshDays) });
            toast.success(`Session policy updated · ${accessMin} min access / ${refreshDays} d refresh`);
            onSave();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="border border-border p-5" data-testid="session-policy-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4 text-primary"/> Session TTL policy</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Access token = how long a user can call APIs before a silent refresh happens. Refresh = how long a user can stay signed-in without re-entering their password.
                    </div>
                </div>
                <button onClick={save} disabled={!dirty || saving} data-testid="session-policy-save"
                    className="text-[10px] uppercase tracking-[0.14em] border border-primary bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 disabled:opacity-40 inline-flex items-center gap-1">
                    <Save className="w-3 h-3"/> {saving ? "Saving…" : "Save"}
                </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Access-token TTL (minutes) · <span className="text-muted-foreground">default {policy.defaults.access_min}</span></label>
                    <input type="number" min={5} max={1440} value={accessMin} onChange={e => setAccessMin(Number(e.target.value))}
                        data-testid="session-policy-access-min"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 mt-1 font-mono text-sm"/>
                    <div className="text-[10px] text-muted-foreground mt-1">5 – 1440 min · shorter = safer, more refresh calls</div>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Refresh-token TTL (days) · <span className="text-muted-foreground">default {policy.defaults.refresh_days}</span></label>
                    <input type="number" min={1} max={30} value={refreshDays} onChange={e => setRefreshDays(Number(e.target.value))}
                        data-testid="session-policy-refresh-days"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 mt-1 font-mono text-sm"/>
                    <div className="text-[10px] text-muted-foreground mt-1">1 – 30 days · after this the user must sign in with password</div>
                </div>
            </div>
            {policy.updated_at && (
                <div className="text-[10px] text-muted-foreground mt-3">
                    Last change: {fmtDateTime(policy.updated_at)} by {policy.updated_by?.email || "system"}
                </div>
            )}
        </div>
    );
}

export default function AdminSessions() {
    const [policy, setPolicy] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [busyUserId, setBusyUserId] = useState(null);
    const [nukeBusy, setNukeBusy] = useState(false);
    const [drawerUser, setDrawerUser] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [pol, sess] = await Promise.all([
                api.get("/admin/session-policy"),
                api.get("/admin/sessions"),
            ]);
            setPolicy(pol.data);
            setRows(sess.data.rows || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter(r =>
            r.email.toLowerCase().includes(term)
            || (r.name || "").toLowerCase().includes(term)
            || (r.tenant_name || "").toLowerCase().includes(term)
            || (r.role || "").toLowerCase().includes(term)
        );
    }, [rows, q]);

    const forceLogout = async (r) => {
        if (!window.confirm(`Force-logout ${r.email}?\n\nAll their sessions will be revoked immediately. They'll need to sign in again.`)) return;
        setBusyUserId(r.user_id);
        try {
            await api.post(`/security/force-logout/${r.user_id}`);
            toast.success(`${r.email} signed out`);
            loadAll();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusyUserId(null); }
    };

    const forceLogoutAll = async () => {
        if (!window.confirm("Force-logout EVERY non-super-admin user across the platform?\n\nThis is an incident-response tool. Super-admin sessions are preserved so you're not locked out. Users will need to sign in again on their next request.")) return;
        setNukeBusy(true);
        try {
            const { data } = await api.post("/admin/sessions/force-logout-all");
            toast.success(`${data.revoked || 0} session(s) revoked`);
            loadAll();
        } catch (e) { toast.error(apiError(e)); }
        finally { setNukeBusy(false); }
    };

    const activeCount = rows.filter(r => !r.revoked).length;
    const revokedCount = rows.filter(r => r.revoked).length;

    return (
        <>
            <PageHeader title="Sessions & Auth"
                subtitle="Live view of who's signed in, session TTL policy, and one-click force-logout controls for incident response."
                action={
                    <button onClick={loadAll} data-testid="sessions-refresh"
                        className="inline-flex items-center gap-2 text-xs border border-border px-3 py-2 hover:border-primary transition">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.6}/> Refresh
                    </button>
                }/>

            {!policy ? (
                <div className="text-sm text-muted-foreground p-8 text-center">Loading…</div>
            ) : (
                <>
                    <div className="grid md:grid-cols-3 gap-3 mb-6">
                        <StatCard label="Active sessions (est.)" value={activeCount} hint={`within last ${policy.refresh_days} days`} testId="stat-active-sessions"/>
                        <StatCard label="Revoked (yet to expire)" value={revokedCount} hint="waiting for token TTL to lapse" testId="stat-revoked-sessions"/>
                        <StatCard label="Current policy" value={`${policy.access_min} min`} hint={`refresh ${policy.refresh_days} d`} testId="stat-current-policy"/>
                    </div>

                    <PolicyCard policy={policy} onSave={loadAll}/>

                    <div className="mt-6"><MessagingDiagnosticsCard/></div>

                    <div className="mt-8 flex items-end justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-medium flex items-center gap-2"><Shield className="w-4 h-4 text-primary"/> Signed-in users</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">Inferred from `last_login_at` (stamped on every sign-in and silent refresh). JWT tokens are stateless, so this is best-effort.</div>
                        </div>
                        <div className="flex gap-2">
                            <input value={q} onChange={e => setQ(e.target.value)}
                                data-testid="sessions-search"
                                placeholder="Search email, name, tenant, role..."
                                className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs min-w-[260px]"/>
                            <button onClick={forceLogoutAll} disabled={nukeBusy}
                                data-testid="sessions-force-logout-all"
                                className="text-[10px] uppercase tracking-[0.14em] border border-rose-500/50 text-rose-400 hover:bg-rose-500/10 px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3"/> {nukeBusy ? "Working…" : "Force-logout all"}
                            </button>
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="mt-4"><EmptyState title="No active sessions" subtitle={q ? "Try a different search." : "Nobody is signed in right now."}/></div>
                    ) : (
                        <div className="border border-border overflow-x-auto mt-4" data-testid="sessions-table">
                            <table className="min-w-[900px] w-full text-sm">
                                <thead className="bg-muted border-b border-border">
                                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                        <th className="px-4 py-3">User</th>
                                        <th className="px-4 py-3">Role</th>
                                        <th className="px-4 py-3">Tenant</th>
                                        <th className="px-4 py-3">Last sign-in</th>
                                        <th className="px-4 py-3">IP</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(r => (
                                        <tr key={r.user_id} data-testid={`session-row-${r.user_id}`}
                                            className="border-b border-border hover:bg-background/40 transition">
                                            <td className="px-4 py-3">
                                                <div>{r.name || r.email}</div>
                                                <div className="text-[11px] text-muted-foreground">{r.email}</div>
                                            </td>
                                            <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{r.role}</td>
                                            <td className="px-4 py-3 text-xs">{r.tenant_name}</td>
                                            <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{fmtDateTime(r.last_login_at)}</td>
                                            <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{r.last_login_ip || "—"}</td>
                                            <td className="px-4 py-3">
                                                {r.revoked
                                                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-400 border border-rose-400/50 rounded-full px-2 py-0.5"><ShieldOff className="w-3 h-3"/> revoked</span>
                                                    : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-400/50 rounded-full px-2 py-0.5"><Shield className="w-3 h-3"/> active</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-4 justify-end">
                                                    <button onClick={() => setDrawerUser(r)}
                                                        data-testid={`session-history-${r.user_id}`}
                                                        className="text-[10px] uppercase tracking-[0.14em] text-primary hover:underline inline-flex items-center gap-1">
                                                        <History className="w-3 h-3"/> History
                                                    </button>
                                                    {r.revoked ? (
                                                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">—</span>
                                                    ) : (
                                                        <button onClick={() => forceLogout(r)}
                                                            disabled={busyUserId === r.user_id || r.role === "super_admin"}
                                                            title={r.role === "super_admin" ? "Cannot force-logout a super-admin from here" : ""}
                                                            data-testid={`session-force-logout-${r.user_id}`}
                                                            className="text-[10px] uppercase tracking-[0.14em] text-rose-400 hover:underline disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
                                                            <LogOut className="w-3 h-3"/> {busyUserId === r.user_id ? "…" : "Force logout"}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
            {drawerUser && (
                <div className="fixed inset-0 z-50 flex" data-testid="login-history-drawer">
                    <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={() => setDrawerUser(null)}/>
                    <div className="relative ml-auto w-full max-w-2xl h-full bg-muted border-l border-border overflow-y-auto">
                        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border bg-muted/95 backdrop-blur">
                            <div>
                                <div className="overline mb-1">Login history</div>
                                <div className="font-serif text-2xl">{drawerUser.name || drawerUser.email}</div>
                                <div className="text-[11px] text-muted-foreground font-mono">{drawerUser.email} · {drawerUser.role} · {drawerUser.tenant_name}</div>
                            </div>
                            <button onClick={() => setDrawerUser(null)} data-testid="login-history-drawer-close"
                                className="text-muted-foreground hover:text-foreground p-2">
                                <X className="w-5 h-5"/>
                            </button>
                        </div>
                        <div className="p-6">
                            <LoginHistoryTimeline
                                endpoint={`/admin/users/${drawerUser.user_id}/login-history`}
                                limit={50}
                                showTitle={false}
                                testIdPrefix="admin-login-history"/>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
